import i18n from './i18n/i18n';

export function formatDuration(duration: number, roundUp = false) {
  if (duration < 0) {
    throw new Error('Positive duration please.');
  }

  duration /= 1000;
  const seconds = Math.floor(duration) % 60;

  duration /= 60;
  const minutes = Math.floor(duration) % 60;

  duration /= 60;
  const hours = Math.floor(duration) % 24;

  // If duration more than a day, just return total days
  duration /= 24;
  if (duration >= 1) {
    const days = roundUp ? Math.ceil(duration) : Math.floor(duration);
    if (days > 1) {
      return i18n('duration.days', { value: days });
    }
    return i18n('duration.day', { value: days });
  }

  // Otherwise return HH:mm:ss
  const hoursStr = hours > 0 ? `${hours}:` : '';
  const minutesPrefix = hours === 0 || minutes >= 10 ? '' : '0';
  const minutesStr = `${minutesPrefix}${minutes}:`;
  const secondsPrefix = seconds >= 10 ? '' : '0';
  const secondsStr = `${secondsPrefix}${seconds}`;

  return `${hoursStr}${minutesStr}${secondsStr}`;
}

/**
 * Formats a duration (in milliseconds) into a human-readable string.
 *
 * - If the duration is greater than or equal to one day, it returns the number of days.
 * - If the duration is less than a day, it returns the time in `HH:mm:ss`
 * - If the duration is more less than one hour, it returns the time in `MMmSSss` format.
 * - If the duration is more more then one hour and less the one day, it returns the time in `HHhMMm` format.
 *
 * @param duration - The duration in milliseconds. Must be a non-negative number.
 * @param roundUp - Optional. If `true`, rounds up the number of days when the duration is greater than or equal to one day. Defaults to `false`.
 * @returns A formatted string representing the duration.
 * @throws Will throw an error if the `duration` is negative.
 */
export function formatDurationShort(duration: number, roundUp = false) {
  if (duration < 0) {
    throw new Error('Positive duration please.');
  }

  duration /= 1000;
  const seconds = Math.floor(duration) % 60;

  duration /= 60;
  const minutes = Math.floor(duration) % 60;

  duration /= 60;
  const hours = Math.floor(duration) % 24;

  // If duration more than a day, just return total days
  duration /= 24;
  if (duration >= 1) {
    const days = roundUp ? Math.ceil(duration) : Math.floor(duration);
    if (days > 1) {
      return i18n('duration.days', { value: days });
    }
    return i18n('duration.day', { value: days });
  }

  // Otherwise return HH:mm:ss
  const hoursStr = hours > 0 ? `${hours}:` : '';
  const minutesPrefix = hours === 0 || minutes >= 10 ? '' : '0';
  const minutesStr = `${minutesPrefix}${minutes}:`;

  // If one or more hours, no need to show seconds.
  if (hours >= 1) {
    return `${hours}h${minutes}m`;
  }

  // Otherwise show seconds
  const secondsPrefix = seconds >= 10 ? '' : '0';
  const secondsStr = `${secondsPrefix}${seconds}`;

  return `${hoursStr}${minutesStr}${secondsStr}`;
}
